import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { ConfirmAlert, showAlert } from '../../components';
import {
  getBiCustomers,
  createBiCustomer,
  updateBiCustomer,
  deleteBiCustomer,
  uploadBiCustomersFile,
  confirmBiCustomers,
  getSalesReps,
  sendBiCustommerMonthlyReport,
} from '@/services/BiCustomerService';
import { useLoading } from '@/utils/LoadingUtil';
import biCustomerModel from '@/model/sales-rep/BiCustomer.model';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '@/lib/handleApiError';

const BiCustomerContext = createContext({
  biCustomerData: [],
  setBiCustomerData: () => {},
  fetchBiCustomerData: () => {},
  clearBiCustomerData: () => {},
});

export const useBiCustomer = () => {
  return useContext(BiCustomerContext);
};

const BiCustomerContextProvider = ({ children }) => {
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [confirmData, setConfirmData] = useState([]);
  const [biCustomerData, setBiCustomerData] = useState([]);
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { biCustomer, setBiCustomer, intitalState } = biCustomerModel();
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const nav = useNavigate();

  useEffect(() => {
    setCurrentPage(0);
    setSearchQuery('');
    setPageSize(100);
    setSortBy('');
    setSortDir('desc');
  }, [nav]);

  const fetchBiCustomerData = useCallback(
    async (
      page = currentPage,
      size = pageSize,
      search = searchQuery,
      sortField = sortBy, // Renamed parameter to avoid shadowing
      sortDirection = sortDir, // Renamed parameter to avoid shadowing
    ) => {
      try {
        startLoading();
        const res = await getBiCustomers(
          page,
          size,
          search,
          sortField, // Use renamed parameter
          sortDirection, // Use renamed parameter
        );
        console.log(res);
        setBiCustomerData(res._embedded?.salesCustomerResponseList || []);
        setTotalElements(res?.page?.totalElements);
      } catch (error) {
        handleApiError('error', error?.response?.data, error?.response?.status);
        setTotalElements(0);
      } finally {
        stopLoading();
      }
    },
    [currentPage, pageSize, searchQuery, startLoading, stopLoading],
  );

  const createCustomer = async (customerData) => {
    try {
      startLoading();
      const newCustomer = await createBiCustomer(customerData);
      setBiCustomerData((prevData) => [...prevData, newCustomer]);
    } catch (error) {
      handleApiError('error', error?.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const updateCustomer = async (customerId, biCustomer) => {
    try {
      startLoading(); // Start loading
      const existingCustomer = biCustomerData.find(
        (customer) => customer.id === customerId,
      ); // Find the existing customer
      const updatedCustomer = { ...existingCustomer, ...biCustomer }; // Merge existing customer with updated fields
      await updateBiCustomer(customerId, updatedCustomer); // Send the full object to the API
      await fetchBiCustomerData(); // Refresh the data
      setBiCustomer(intitalState);
    } catch (error) {
      handleApiError(
        'error',
        error?.response?.data || 'An error occurred',
        error?.response?.status,
      );
    } finally {
      stopLoading(); // Stop loading
    }
  };

  const deleteCustomer = useCallback(
    async (id) => {
      const result = await ConfirmAlert({ confirmBtnText: 'Delete Customer' });
      if (!result.isConfirmed) {
        showAlert('error', 'Customer deletion cancelled');
        return;
      }

      startLoading();
      try {
        await deleteBiCustomer(id);
        fetchBiCustomerData();
      } catch (error) {
        handleApiError(
          'error',
          error.message || 'An error occurred',
          error?.response?.status,
        );
      } finally {
        stopLoading();
      }
    },
    [fetchBiCustomerData, startLoading, stopLoading],
  );

  const uploadCustomerFile = async (file) => {
    startLoading();
    try {
      const res = await uploadBiCustomersFile(file);
      setConfirmData(res.updates);
      if (res.updates.length > 0) {
        showAlert('success', 'Customers uploaded successfully');
      }
    } catch (error) {
      // handleApiError('error', error.response.data);
      setConfirmData([]);
    } finally {
      stopLoading();
    }
  };

  const confirmBiCustomersApi = async (data, closeModal) => {
    try {
      startLoading();
      const res = await confirmBiCustomers(data);
      showAlert('success', res.data || 'Customers confirmed successfully');
      setConfirmData([]);
      closeModal();
    } catch (error) {
      handleApiError(
        'error',
        error.response?.data || 'An error occurred',
        error?.response?.status,
      );
    } finally {
      stopLoading();
    }
  };

  const getAllSalesReps = async () => {
    startLoading();
    try {
      const response = await getSalesReps();
      return response;
    } catch (error) {
      handleApiError('error', error?.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const sendBiCustommerMonthlyReportApi = async () => {
    try {
      startLoading();
      const res = await sendBiCustommerMonthlyReport();
      showAlert('success', res.data || 'Report sent successfully');
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const clearBiCustomerData = () => {
    setBiCustomer(intitalState);
  };

  return (
    <BiCustomerContext.Provider
      value={{
        biCustomerData,
        setBiCustomerData,
        fetchBiCustomerData,
        clearBiCustomerData,
        totalElements,
        setTotalElements,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        searchQuery,
        setSearchQuery,
        error,
        setError,
        isLoading,
        createCustomer,
        updateCustomer,
        deleteCustomer,
        uploadCustomerFile,
        confirmData,
        setConfirmData,
        confirmBiCustomersApi,
        biCustomer,
        setBiCustomer,
        intitalState,
        getAllSalesReps,
        sendBiCustommerMonthlyReportApi,
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
      }}
    >
      {children}
    </BiCustomerContext.Provider>
  );
};

export default BiCustomerContextProvider;
